import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useLocation, useNavigate } from 'react-router-dom'

import { DrugGroupCreateForm } from '@/apps/latest/components/molecules/DrugGroupCreateForm'
import DrugGroupTable from '@/apps/latest/components/portal/DrugGroupTable'
import PaginationControl from '@/apps/latest/components/portal/PaginationControl'
import SearchBox from '@/apps/latest/components/portal/SearchBox'
import {
  Banner,
  DialogShell,
  NotionButton,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import {
  useCreateDrugGroupMutation,
  useDrugGroupsQuery,
} from '@/features/internal/drugs'

const PAGE_SIZE = 20

/**
 * 医薬品グループ一覧。
 * グループ作成はこのページ上のダイアログ (名前+説明のみ)、
 * 所属薬剤・SOAPテンプレートの整備は詳細ページで行う (ADR-0017)。
 */
const DrugGroupListPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const search = queryParams.get('search') || ''
  const page = parseInt(queryParams.get('page') || '1', 10)

  const [searchInput, setSearchInput] = useState(search)
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading, error } = useDrugGroupsQuery({ page, search })
  const createMutation = useCreateDrugGroupMutation()

  const groups = data?.results ?? []
  const totalCount = data?.count ?? 0
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / (data?.page_size || PAGE_SIZE))
  )

  const updateParams = (nextSearch: string, nextPage: number) => {
    const params = new URLSearchParams()
    if (nextSearch) params.set('search', nextSearch)
    if (nextPage > 1) params.set('page', String(nextPage))
    const qs = params.toString()
    navigate(qs ? `/portal/soaps?${qs}` : '/portal/soaps', { replace: true })
  }

  const handleCreate = async (name: string, description: string) => {
    try {
      const created = await createMutation.mutateAsync({ name, description })
      setCreateOpen(false)
      navigate(`/portal/soaps/groups/${created.id}?edit=true`)
    } catch {
      // 失敗 toast は mutation 側 (onError)。dialog は開いたまま再試行できる
    }
  }

  if (error) {
    return (
      <PageScaffold title="医薬品グループ">
        <Banner severity="danger">
          データの取得に失敗しました。時間をおいて再度お試しください。
        </Banner>
      </PageScaffold>
    )
  }

  return (
    <PageScaffold
      title="医薬品グループ"
      meta={totalCount > 0 ? `全 ${totalCount} 件` : undefined}
      actions={
        <NotionButton
          variant="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          新規グループ
        </NotionButton>
      }
    >
      <Box sx={{ maxWidth: 320 }}>
        <SearchBox
          placeholder="グループ名で検索"
          value={searchInput}
          onChange={setSearchInput}
          onSearch={() => updateParams(searchInput, 1)}
          variant="standard"
        />
      </Box>

      <DrugGroupTable
        groups={groups}
        loading={isLoading}
        onRowClick={(groupId) => navigate(`/portal/soaps/groups/${groupId}`)}
      />

      <PaginationControl
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(nextPage) => updateParams(search, nextPage)}
      />

      <DialogShell
        open={createOpen}
        onClose={() => {
          if (!createMutation.isPending) setCreateOpen(false)
        }}
        title="グループ作成"
        maxWidth="sm"
        disabled={createMutation.isPending}
      >
        <DrugGroupCreateForm
          onSubmit={handleCreate}
          onCancel={() => setCreateOpen(false)}
          isLoading={createMutation.isPending}
        />
      </DialogShell>
    </PageScaffold>
  )
}

export default DrugGroupListPage
